import { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useLanguage } from '@/contexts/LanguageContext';

interface NewsItem {
  id: string;
  title: {
    ar: string;
    en: string;
  };
  excerpt: {
    ar: string;
    en: string;
  };
  content: {
    ar: string;
    en: string;
  };
  date: string;
  image: string;
  author: string | { ar: string; en: string; };
  category: string | { ar: string; en: string; };
  order: number;
}

export function LandingNews() {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const { t, language } = useLanguage();

  // Create infinite loop by triplicating the array (reversed order)
  const infiniteNews = newsItems.length > 0 
    ? [...newsItems.slice().reverse(), ...newsItems.slice().reverse(), ...newsItems.slice().reverse()] 
    : [];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const newsQuery = query(
        collection(db, 'landing_news'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(newsQuery);

      const newsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as NewsItem[];

      setNewsItems(newsData);
    } catch (err) {
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize scroll to middle set after component loads
  useEffect(() => {
    if (newsItems.length > 0 && containerRef.current) {
      const container = containerRef.current;
      // Wait for layout to complete
      setTimeout(() => {
        const cardElement = container.children[0] as HTMLElement;
        if (cardElement) {
          // Scroll to the start of the middle set (second copy)
          const middlePosition = cardElement.offsetWidth * newsItems.length;
          container.scrollLeft = middlePosition;
        }
      }, 100);
    }
  }, [newsItems.length]);

  // Monitor scroll position and reset if needed for infinite effect
  useEffect(() => {
    const container = containerRef.current;
    if (!container || newsItems.length === 0) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const cardElement = container.children[0] as HTMLElement;
      if (!cardElement) return;

      const cardWidth = cardElement.offsetWidth;
      const scrollLeft = container.scrollLeft;
      const totalOriginalWidth = cardWidth * newsItems.length;

      // If scrolled past the end of second set, jump to start of second set
      if (scrollLeft >= totalOriginalWidth * 2) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
      // If scrolled before the start of second set, jump to end of second set
      else if (scrollLeft < totalOriginalWidth) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth * 2 - cardWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [newsItems.length]);

  // Auto-scroll effect
  useEffect(() => {
    if (newsItems.length === 0) return;

    const interval = setInterval(() => {
      if (!isHovering && containerRef.current && !isScrollingRef.current) {
        const container = containerRef.current;
        const cardElement = container.children[0] as HTMLElement;
        if (!cardElement) return;
        
        const scrollAmount = cardElement.offsetWidth;
        
        // Scroll left (decrease scrollLeft) - the infinite scroll handler will take care of wrapping
        container.scrollTo({ 
          left: container.scrollLeft - scrollAmount, 
          behavior: 'smooth' 
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isHovering, newsItems.length]);

  const handleNavigation = (direction: 'prev' | 'next') => {
    const container = containerRef.current;
    if (!container) return;

    const cardElement = container.children[0] as HTMLElement;
    if (!cardElement) return;
    
    const scrollAmount = cardElement.offsetWidth;
    
    if (direction === 'next') {
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    } else {
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">{t('landing.common.loading')}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="news" className="py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-[#004250] mb-12">{t('landing.news.title')}</h2>
        
        <div 
          className="relative group"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div 
            ref={containerRef}
            dir="ltr"
            className="flex overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {infiniteNews.map((item, index) => {
              const title = item.title[language] || item.title.ar;
              const excerpt = item.excerpt[language] || item.excerpt.ar;
              
              // Safe rendering for author and category with proper language selection
              const author = typeof item.author === 'string' 
                ? item.author 
                : (typeof item.author === 'object' ? (item.author[language] || item.author.ar || item.author.en || '') : '');
              const category = typeof item.category === 'string' 
                ? item.category 
                : (typeof item.category === 'object' ? (item.category[language] || item.category.ar || item.category.en || '') : '');
              
              return (
              <div
                key={`${item.id}-${index}`}
                className="min-w-full md:min-w-[33.333%] px-4 py-8"
              >
                <div className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <img src={item.image} alt={title} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <span className="inline-block bg-[#7dda55] text-white px-3 py-1 rounded-full text-sm mb-2">
                      {category}
                    </span>
                    <div className="text-sm text-gray-500 mb-2">{item.date}</div>
                    <h3 className="text-xl font-bold text-[#004250] mb-2">{title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{excerpt}</p>
                    <button className="text-[#7dda55] hover:text-[#004250] font-semibold">
                      {t('landing.common.readMore')} ←
                    </button>
                  </div>
                </div>
              </div>
            )}
            )}
          </div>

          <button
            onClick={() => handleNavigation('next')}
            className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <button
            onClick={() => handleNavigation('prev')}
            className="absolute left-0 top-1/2 -translate-y-1/2 translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        </div>
      </div>
    </section>
  );
}

